const DRAG_THRESHOLD = 5;

/**
 * Makes an overflowing horizontal scroller draggable with a mouse.
 *
 * Touch scrolling is left native: only `mouse` pointers are intercepted. A drag
 * past the threshold swallows the click that follows, so dragging from a link
 * does not navigate.
 */
class TdDragScroll extends HTMLElement {
  /** @type {number | null} */
  pointerId = null;

  /** @type {boolean} */
  dragging = false;

  /** @type {boolean} */
  suppressClick = false;

  /** @type {number} */
  startX = 0;

  /** @type {number} */
  startScrollLeft = 0;

  /** @type {ResizeObserver | undefined} */
  resizeObserver;

  connectedCallback() {
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.addEventListener('dragstart', this.onDragStart);
    this.addEventListener('click', this.onClick, true);

    this.resizeObserver = new ResizeObserver(this.updateOverflow);
    this.resizeObserver.observe(this);
    this.updateOverflow();
  }

  disconnectedCallback() {
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerUp);
    this.removeEventListener('dragstart', this.onDragStart);
    this.removeEventListener('click', this.onClick, true);
    this.resizeObserver?.disconnect();
  }

  /** @returns {boolean} */
  get overflowing() {
    return this.scrollWidth - this.clientWidth > 1;
  }

  updateOverflow = () => {
    this.toggleAttribute('data-td-scrollable', this.overflowing);
  };

  /**
   * @param {PointerEvent} event
   */
  onPointerDown = (event) => {
    this.suppressClick = false;
    if (event.pointerType !== 'mouse' || event.button !== 0 || !this.overflowing) return;

    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startScrollLeft = this.scrollLeft;
    this.dragging = false;
  };

  /**
   * @param {PointerEvent} event
   */
  onPointerMove = (event) => {
    if (this.pointerId !== event.pointerId) return;

    const distance = event.clientX - this.startX;

    if (!this.dragging) {
      if (Math.abs(distance) < DRAG_THRESHOLD) return;
      this.dragging = true;
      this.setPointerCapture(event.pointerId);
      this.toggleAttribute('data-td-dragging', true);
    }

    this.scrollLeft = this.startScrollLeft - distance;
  };

  /**
   * @param {PointerEvent} event
   */
  onPointerUp = (event) => {
    if (this.pointerId !== event.pointerId) return;

    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    this.pointerId = null;
    this.toggleAttribute('data-td-dragging', false);

    // A drag must not follow the link it started on.
    if (this.dragging) this.suppressClick = true;
    this.dragging = false;
  };

  /**
   * @param {MouseEvent} event
   */
  onClick = (event) => {
    if (!this.suppressClick) return;
    this.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * @param {DragEvent} event
   */
  onDragStart = (event) => {
    if (this.pointerId !== null) event.preventDefault();
  };
}

if (!customElements.get('td-drag-scroll')) {
  customElements.define('td-drag-scroll', TdDragScroll);
}
